import { createSelector } from '@reduxjs/toolkit';

import { compareId } from 'mastodon/compare_id';
import type { NotificationGroup } from 'mastodon/models/notification_group';
import type { NotificationGap } from 'mastodon/reducers/notification_groups';
import type { RootState } from 'mastodon/store';

import {
  selectSettingsNotificationsExcludedTypes,
  selectSettingsNotificationsQuickFilterActive,
  selectSettingsNotificationsQuickFilterShow,
} from './settings';

const filterNotificationsByAllowedTypes = (
  showFilterBar: boolean,
  allowedType: string,
  excludedTypes: string[],
  notifications: (NotificationGroup | NotificationGap)[],
  directMentionIds: string,
) => {
  // The Mentions filter leaves DMs out and the DM filter keeps only them.
  // The server does so too, but the notifications already loaded are shown
  // until its answer arrives.
  const directIds = new Set(directMentionIds.split(','));
  const isDirectMessage = (item: NotificationGroup) =>
    item.type === 'mention' && !!item.statusId && directIds.has(item.statusId);

  if (!showFilterBar || allowedType === 'all') {
    // used if user changed the notification settings after loading the notifications from the server
    // otherwise a list of notifications will come pre-filtered from the backend
    // we need to turn it off for FilterBar in order not to block ourselves from seeing a specific category
    return notifications.filter(
      (item) => item.type === 'gap' || !excludedTypes.includes(item.type),
    );
  }
  return notifications.filter(
    (item) =>
      item.type === 'gap' ||
      (allowedType === item.type &&
        !(allowedType === 'mention' && isDirectMessage(item))) ||
      (allowedType === 'mention' && item.type === 'quote') ||
      (allowedType === 'direct' && isDirectMessage(item)) ||
      (allowedType === 'collection' &&
        (item.type === 'collection_update' ||
          item.type === 'added_to_collection')),
  );
};

// The mention notifications that are DMs, as a string so that the filters
// below run again only when that list changes, not when any post changes
const selectDirectMentionIds = createSelector(
  [
    (state: RootState) => state.notificationGroups.groups,
    (state: RootState) => state.notificationGroups.pendingGroups,
    (state: RootState) => state.statuses,
  ],
  (groups, pendingGroups, statuses) =>
    [...groups, ...pendingGroups]
      .filter(
        (item) =>
          item.type === 'mention' &&
          statuses.getIn([item.statusId, 'visibility']) === 'direct',
      )
      .map((item) => (item.type === 'mention' ? item.statusId : ''))
      .join(','),
);

export const selectNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,
    selectSettingsNotificationsQuickFilterActive,
    selectSettingsNotificationsExcludedTypes,
    (state: RootState) => state.notificationGroups.groups,
    selectDirectMentionIds,
  ],
  filterNotificationsByAllowedTypes,
);

const selectPendingNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,
    selectSettingsNotificationsQuickFilterActive,
    selectSettingsNotificationsExcludedTypes,
    (state: RootState) => state.notificationGroups.pendingGroups,
    selectDirectMentionIds,
  ],
  filterNotificationsByAllowedTypes,
);

export const selectUnreadNotificationGroupsCount = createSelector(
  [
    (s: RootState) => s.notificationGroups.lastReadId,
    selectNotificationGroups,
    selectPendingNotificationGroups,
  ],
  (notificationMarker, groups, pendingGroups) => {
    return (
      groups.filter(
        (group) =>
          group.type !== 'gap' &&
          group.page_max_id &&
          compareId(group.page_max_id, notificationMarker) > 0,
      ).length +
      pendingGroups.filter(
        (group) =>
          group.type !== 'gap' &&
          group.page_max_id &&
          compareId(group.page_max_id, notificationMarker) > 0,
      ).length
    );
  },
);

// Whether there is any unread notification according to the user-facing state
export const selectAnyPendingNotification = createSelector(
  [
    (s: RootState) => s.notificationGroups.readMarkerId,
    selectNotificationGroups,
  ],
  (notificationMarker, groups) => {
    return groups.some(
      (group) =>
        group.type !== 'gap' &&
        group.page_max_id &&
        compareId(group.page_max_id, notificationMarker) > 0,
    );
  },
);

export const selectPendingNotificationGroupsCount = createSelector(
  [selectPendingNotificationGroups],
  (pendingGroups) =>
    pendingGroups.filter((group) => group.type !== 'gap').length,
);

// Unread notifications for the app icon badge, whatever the notification
// filter shows; DMs are left out, as unread chats are counted on their own
export const selectAppBadgeNotificationCount = createSelector(
  [
    (s: RootState) => s.notificationGroups.lastReadId,
    (s: RootState) => s.notificationGroups.groups,
    (s: RootState) => s.notificationGroups.pendingGroups,
    selectSettingsNotificationsExcludedTypes,
    selectDirectMentionIds,
  ],
  (notificationMarker, groups, pendingGroups, excludedTypes, directMentionIds) => {
    const directIds = new Set(directMentionIds.split(','));

    return [...groups, ...pendingGroups].filter(
      (group) =>
        group.type !== 'gap' &&
        !!group.page_max_id &&
        compareId(group.page_max_id, notificationMarker) > 0 &&
        !excludedTypes.includes(group.type) &&
        !(
          group.type === 'mention' &&
          !!group.statusId &&
          directIds.has(group.statusId)
        ),
    ).length;
  },
);

// Changes whenever a DM arrives as a notification
export const selectDirectMentionKey = selectDirectMentionIds;
